import { Component, OnInit, OnDestroy, Inject, AfterViewInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router, NavigationEnd, ActivatedRoute, NavigationStart, ActivationStart } from '@angular/router';
import { TranslationService } from '@cemex-core/angular-localization-v7';
import { Store } from '@ngrx/store';
import { Subject, Subscription } from 'rxjs';
import { map, takeUntil, filter, tap, distinctUntilChanged, skip } from 'rxjs/operators';

import * as fromCoreState from "./../../core/reducers/index";
import * as LegalEntityActions from "./../../core/actions/legalEntity.action";
import { AppInsightsService } from "../../services/app-insights.service";
import { environment } from "../../../environments/environment";
import { cobrowse } from "@features/cobrowse";
import { IpcFeatureToggleService } from "../../../cemexng2components/components/feature-toggle/feature-toggle.service";
import { StorageManagerService } from '@app/core/services/storage-manager.service';
import { APP_VERSION_TOKEN, APP_BUILD_DATE_TOKEN, APP_BUILD_COMMIT_TOKEN } from '@app/singletons.module';
import { CountlyService, SessionService } from '@cemex-core/angular-services-v8';
import { IamAuthService } from '@cemex-core/msal-services-v1';
import { dashboard } from '../../../rollout-containers';

@Component({
  selector: "app",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  private destroyNotifier$: Subject<void> = new Subject();
  private routerSubcription$!: Subscription;
  private USER_REGIONS = {
    AE: "AMEA",
    EG: "AMEA",
    IL: "AMEA",
    PH: "AMEA",
    CZ: "Europe",
    DE: "Europe",
    ES: "Europe",
    FR: "Europe",
    GB: "Europe",
    PL: "Europe",
    MX: "Mexico",
    CO: "SCA&CC",
    CR: "SCA&CC",
    DO: "SCA&CC",
    GT: "SCA&CC",
    NI: "SCA&CC",
    PA: "SCA&CC",
    PE: "SCA&CC",
    PR: "SCA&CC",
    SV: "SCA&CC",
    US: "US",
  };

  public cobrowseLicense: string = window["COBROWSE_KEY"] || environment.cobrowseKey;
  public showCobrowse = false;
  public qualtricsId = "Q_ZID=ZN_557ZiRciIgb8rFY";
  private skipQASI: boolean = false;
  constructor(
    private store$: Store<fromCoreState.State>,
    private translation: TranslationService,
    private countlyService: CountlyService,
    private sessionService: SessionService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private appInsights: AppInsightsService,
    private featureService: IpcFeatureToggleService,
    private storageManagerService: StorageManagerService,
    @Inject(APP_VERSION_TOKEN) private appVersion: string,
    @Inject(APP_BUILD_DATE_TOKEN) private appBuildDate: string,
    @Inject(APP_BUILD_COMMIT_TOKEN) private appBuildCommit: string,
    private iamService: IamAuthService,
    private title: Title
  ) {
    try {
      const $htmlElement = document.getElementsByTagName("html");
      if (this.translation.selectedLanguage.rtl) {
        $htmlElement[0].dir = "rtl";
      } else {
        $htmlElement[0].dir = "ltr";
      }
    } catch ($exception) {
      console.error($exception);
    }
    const countlyKey = window["COUNTLY_KEY"];
    const countlyUrl = window["COUNTLY_URL"];
    const user = this.getUserInfoForCountly();
    this.countlyService.startService(countlyUrl, countlyKey);

    //TODO: Abstract away to countly service
    if (this.countlyService.Countly && user) {
      let index = -1;
      this.countlyService.Countly.q.find(function (element, i) {
        if (Array.isArray(element) && element.length > 0 && element[0] == "track_pageview") {
          index = i;
        }
      });
      if (index != -1) {
        this.countlyService.Countly.q.splice(index, 1);
      }

      this.countlyService.Countly.app_version = this.appVersion;
      this.countlyService.identifyUser(user);
    }
  }

  ngAfterViewInit(): void {
    this.writeBuildInfoIntoHTML();
  }

  ngOnInit(): void {
    this.updateDocumentBranding();
    this.featureService.customPropertiesChanged
      .pipe(takeUntil(this.destroyNotifier$))
      .subscribe(() => this.updateDocumentBranding());
    this.iamService.handleMsalEvents();
    this.appInsights.loadAppInsights();
    this.store$.dispatch(new LegalEntityActions.LoadLegalEntity());
    this.sendDataToGoogleTagManager();
    this.checkCobrowseEnable();
    // Subcribe to events methods to render the script when is changed the route
    this.routerSubcription$ = this.router.events.subscribe((event: any) => {
      if (event instanceof NavigationStart) {
        if (!this.skipQASI) {
          this.InicializeQSI();
        }
      }
      if (event instanceof ActivationStart) {
        this.skipQASI = event.snapshot.data["skipQSI"] ? event.snapshot.data["skipQSI"] : false;
      }
    });
  }

  ngOnDestroy(): void {
    this.routerSubcription$.unsubscribe();
    this.destroyNotifier$.next();
  }

  private writeBuildInfoIntoHTML(): void {
    const buildDate = new Date(this.appBuildDate);
    const formattedDate = `${buildDate.getFullYear()}${(buildDate.getMonth() + 1).toString().padStart(2,'0')}${buildDate.getDate().toString().padStart(2,'0')}${buildDate.getHours()}${buildDate.getMinutes()}`;
    const build = `${this.appVersion}+${this.appBuildCommit.substring(0,7)}-${formattedDate}`;
    document.getElementsByTagName('html')[0].setAttribute('data-build', build);
  }

  private updateDocumentBranding(): void {
    const usePagosEnLinea = dashboard.enabledPagoEnLinea.isEnabled();
    this.title.setTitle(usePagosEnLinea ? "Pagos en linea" : "Cemex GO");

    const favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement | null;
    if (usePagosEnLinea && favicon) {
      favicon.href = "assets/favicon.ico";
    }
  }

  InicializeQSI(): void {
    this.loadScript(
      "https://zn9gh8ipzamlwxgmq-cemexglobal.siteintercept.qualtrics.com/SIE/?" + this.qualtricsId,
      function () {
        // The qualtrics api is instantiated and run
        /** @type {?} */
        var QAPI = window["QSI"].API;
        if (QAPI) {
          QAPI.unload();
          QAPI.load();
          QAPI.run();
        }
      }
    );
  }

  loadScript(url, callback) {
    /** @type {?} */
    var script = document.createElement("script");
    script.type = "text/javascript";
    script.onload = function () {
      callback();
    };
    script.src = url;
    document.getElementsByTagName("head")[0].appendChild(script);
  }

  private sendDataToGoogleTagManager(): void {
    const legalEntityTypeCode$ = this.sessionService.currentLegalEntity.pipe(
      map((legalEntity) => legalEntity && legalEntity.legalEntityTypeCode),
      filter((legalEntityTypeCode) => legalEntityTypeCode != null),
      distinctUntilChanged(),
      skip(1),
      tap((legalEntityTypeCode) => this.countlyService.addGTMCustomerChange(legalEntityTypeCode)),
      takeUntil(this.destroyNotifier$)
    );
    legalEntityTypeCode$.subscribe();

    const router$ = this.router.events.pipe(
      filter((routerEvent: NavigationEnd) => routerEvent instanceof NavigationEnd),
      map(() => {
        let childRoute = this.activatedRoute.firstChild;
        while (childRoute) {
          if (childRoute.firstChild) {
            childRoute = childRoute.firstChild;
          } else if (childRoute.snapshot) {
            return childRoute.snapshot.data;
          } else {
            return undefined;
          }
        }
      }),
      filter((data) => data !== undefined),
      tap((routerData) => {
        this.countlyService.addGTM("Invoice and Payments", routerData.routeName);
      }),
      takeUntil(this.destroyNotifier$)
    );
    router$.subscribe();
  }

  private getUserInfoForCountly() {
    const user = this.storageManagerService.user_profile;

    if (user) {
      return {
        email: "",
        // if user has email as userAccount,so use userAcount, otherwise user UserId
        // userId: user.userAccount.indexOf("@") > -1 ? user.userAccount : user.userId,
        // name: user.userAccount.indexOf("@") > -1 ? user.userAccount : user.userId,
        phone: "",
        username: "",
      };
    } else {
      return;
    }
  }

  public getRegion(countryCode: string): string {
    if (countryCode && countryCode.length > 0) {
      return this.USER_REGIONS[countryCode];
    }
    return "";
  }

  private checkCobrowseEnable() {
    this.featureService.customPropertiesChanged.subscribe(() => {
      this.showCobrowse = cobrowse.enable_cobrowse.isEnabled();
    });
  }
}
